/**
 * ProcedureSummary — the generic, specialty-NEUTRAL procedure summary shown to
 * doctors whose specialty has no chart plugin (useChartType() === null). A flat
 * per-procedure list (status pills + rows with expiry / Expired tag, attribution,
 * and the same Start/Note/Cancel/Extend actions + View popover the dental chart
 * uses).
 *
 * TODO(neutral-refactor): this reuses dental-namespaced pieces (RowActionToolbar,
 * ProcedureViewPopover) + dentalHistoryStore. The pluggable-chart refactor should
 * move those to a neutral module and rename the store to patientClinicalStore.
 */
import React, { useEffect, useMemo, useState } from 'react';

import useChartType, { useChartTypeReady } from '../../../charts/core/useChartType';
import useDentalHistoryStore from '../../../stores/dentalHistoryStore';
import ProcedureViewPopover from './dental/ProcedureViewPopover';
import RowActionToolbar from './dental/RowActionToolbar';
import {
  STATUS_META,
  humanSittingStatus,
} from './dental/summaryHelpers';
import { statusFillColor } from './dental/statusFill';
import { buildProcedureSummary, PROCEDURE_PILLS } from './procedureSummaryProjection';

const TODAY = () => new Date().toISOString().slice(0, 10);

function prettyDate(iso) {
  if (!iso) return '';
  const d = new Date(String(iso).slice(0, 10));
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function ProcedureRow({ row, isDark, onView, patientId, addOptimisticSitting, onRefresh }) {
  const muted = isDark ? 'text-slate-400' : 'text-slate-500';
  const exp = row.expiresAt ? String(row.expiresAt).slice(0, 10) : null;
  const isExpired = exp && exp < TODAY();

  return (
    <div className={[
      'rounded-md border px-2.5 py-2',
      isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-300',
    ].join(' ')}
    >
      <button
        type="button"
        onClick={() => onView([row], row.name)}
        className="inline-flex items-center gap-1.5 flex-wrap text-left hover:underline focus:underline cursor-pointer"
        title="View procedure details"
        aria-label={`View ${row.name}`}
      >
        <span
          className="inline-block w-1.5 h-1.5 rounded-full"
          style={{ backgroundColor: statusFillColor(row.status) }}
          aria-hidden="true"
        />
        <span className={['font-medium', isDark ? 'text-slate-100' : 'text-slate-800'].join(' ')}>
          {row.name}
        </span>
        <span className={muted}>{' · '}{humanSittingStatus(row.status)}</span>
        {row.assignee && <span className={muted}>{' · '}{row.assignee}</span>}
        {row.originDoctor && <span className={muted}>{' · proposed by '}{row.originDoctor}</span>}
        {row.bucket === 'planned' && (exp || row.standing) && (
          <span className={muted}>
            {' · '}
            {row.standing
              ? (exp ? `review ${prettyDate(exp)}` : 'standing · no expiry')
              : `valid till ${prettyDate(exp)}`}
          </span>
        )}
        {isExpired && row.bucket === 'planned' && (
          <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-rose-100 text-rose-700 border border-rose-300 dark:bg-rose-900/40 dark:text-rose-200 dark:border-rose-700">
            Expired
          </span>
        )}
        {row.bucket === 'cancelled' && row.cancelledBy && (
          <span className={muted}>{' · cancelled by '}{row.cancelledBy}</span>
        )}
        {row.finalCost != null && row.finalCost > 0 && (
          <span className={muted}>{' · ₹'}{Number(row.finalCost).toLocaleString('en-IN')}</span>
        )}
      </button>

      {row.sittingId && row.bucket !== 'cancelled' && (
        <div className="mt-1.5">
          <RowActionToolbar
            plan={row}
            patientId={patientId}
            addOptimisticSitting={addOptimisticSitting}
            onRefresh={onRefresh}
            isDark={isDark}
            // Start/Complete happen in the Procedures panel/queue below (payment
            // gates Start); the summary keeps Note/Extend/Cancel only.
            canStartComplete={false}
          />
        </div>
      )}
    </div>
  );
}

export default function ProcedureSummary({ visit, isDark = false }) {
  const patientId = visit?.patient_id || null;
  // Self-gate (mirrors OdontogramSection): render ONLY for doctors whose
  // specialty has no chart plugin, and only once specialty has resolved (so a
  // dentist never sees this flash). All hooks run first (rules-of-hooks).
  const chartType = useChartType();
  const chartReady = useChartTypeReady();

  const slot = useDentalHistoryStore((s) => (patientId ? s.byPatient[patientId] : null));
  const subscribe = useDentalHistoryStore((s) => s.subscribe);
  const load = useDentalHistoryStore((s) => s.load);
  const refresh = useDentalHistoryStore((s) => s.refresh);
  const addOptimisticSitting = useDentalHistoryStore((s) => s.addOptimisticSitting);

  useEffect(() => {
    if (!patientId) return undefined;
    const unsub = subscribe(patientId);
    load(patientId);
    return unsub;
  }, [patientId, subscribe, load]);

  // Merge optimistic pending sittings over the server list (immediate feedback).
  const sittings = useMemo(() => {
    const base = slot?.sittings || [];
    const pending = slot?.pendingSittings || [];
    if (!pending.length) return base;
    const byId = new Map(base.map((s) => [s.sitting_id, s]));
    for (const p of pending) {
      byId.set(p.sitting_id, { ...(byId.get(p.sitting_id) || {}), ...p });
    }
    return Array.from(byId.values());
  }, [slot]);

  const { rows, counts } = useMemo(() => buildProcedureSummary(sittings), [sittings]);

  const [expanded, setExpanded] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewingPlans, setViewingPlans] = useState(null);
  const [viewingLabel, setViewingLabel] = useState('');

  const pills = ['all', ...PROCEDURE_PILLS];
  const visible = statusFilter === 'all'
    ? rows : rows.filter((r) => r.bucket === statusFilter);

  const cardClass = [
    'rounded-md border p-3',
    isDark ? 'bg-slate-900 border-slate-700 text-slate-200'
           : 'bg-white border-slate-300 text-slate-800',
  ].join(' ');

  if (!patientId) return null;
  // Dentists (and any specialty with a chart) get their chart's summary; wait
  // until specialty resolves so this never flashes for them.
  if (!chartReady || chartType === 'dental') return null;
  // Nothing to summarise → no button (the QUEUE / Add-procedure list handles it).
  if (!rows.length) return null;

  return (
    <div className={cardClass}>
      {/* Collapsed behind a button — expand to see the per-procedure summary. */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className={[
          'w-full flex items-center justify-between gap-2 text-left',
          isDark ? 'text-slate-100' : 'text-slate-800',
        ].join(' ')}
      >
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold">
          <span aria-hidden="true">🗂</span>
          Procedure summary
          <span className={[
            'px-1.5 py-0.5 rounded-full text-[11px] font-medium',
            isDark ? 'bg-slate-700 text-slate-100' : 'bg-slate-200 text-slate-700',
          ].join(' ')}
          >
            {counts.all}
          </span>
        </span>
        <span className={isDark ? 'text-slate-400' : 'text-slate-500'} aria-hidden="true">
          {expanded ? '▴' : '▾'}
        </span>
      </button>

      {expanded && (
      <div className="mt-3">
      <div className="flex items-center gap-1.5 flex-wrap mb-3">
        {pills.map((id) => {
          const active = statusFilter === id;
          const label = id === 'all' ? 'All' : STATUS_META[id]?.label || id;
          const dot = id !== 'all' ? STATUS_META[id]?.dotClass : null;
          const n = counts[id] ?? 0;
          return (
            <button
              key={id}
              type="button"
              onClick={() => setStatusFilter(id)}
              className={[
                'inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] border',
                active
                  ? 'bg-teal-600 text-white border-teal-700'
                  : (isDark
                    ? 'bg-slate-800 text-slate-200 border-slate-600 hover:bg-slate-700'
                    : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100'),
              ].join(' ')}
            >
              {dot && <span className={['inline-block w-1.5 h-1.5 rounded-full', dot].join(' ')} />}
              {label} · {n}
            </button>
          );
        })}
      </div>

      <div className="space-y-2">
        {visible.map((row) => (
          <ProcedureRow
            key={row.sittingId}
            row={row}
            isDark={isDark}
            patientId={patientId}
            addOptimisticSitting={(s) => addOptimisticSitting(patientId, s)}
            onRefresh={() => refresh(patientId)}
            onView={(plans, label) => { setViewingPlans(plans); setViewingLabel(label); }}
          />
        ))}
        {visible.length === 0 && (
          <div className={['text-xs italic', isDark ? 'text-slate-400' : 'text-slate-500'].join(' ')}>
            No {statusFilter === 'all' ? '' : STATUS_META[statusFilter]?.label?.toLowerCase() || statusFilter}
            {' '}procedures.
          </div>
        )}
      </div>

      {viewingPlans && viewingPlans.length > 0 && (
        <ProcedureViewPopover
          plans={viewingPlans}
          tokenLabel={viewingLabel}
          onClose={() => setViewingPlans(null)}
          isDark={isDark}
        />
      )}
      </div>
      )}
    </div>
  );
}
